import { useCallback, useEffect, useMemo, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useProperty } from "@/contexts/PropertyContext";
import { useRealtime } from "@/contexts/RealtimeContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Vote, Plus, CheckCircle, X, Users2, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { PollCommentsThread } from "@/components/PollCommentsThread";

const money = (n) => (n == null ? "" : ` · $${Number(n).toLocaleString()}`);

function PollBar({ option, tally, poll, myVote, onVote, disabled }) {
  const count = tally?.by_option?.[option.id] || 0;
  const pct = (tally?.total_votes || 0) > 0 ? (count / tally.total_votes) * 100 : 0;
  const isMine = myVote === option.id;
  return (
    <button
      onClick={() => !disabled && onVote(option.id)}
      disabled={disabled}
      className={
        "relative w-full rounded-md border px-3 py-2 text-left transition group " +
        (isMine
          ? "border-emerald-500/40 bg-emerald-500/[0.06]"
          : "border-white/10 hover:border-white/25 hover:bg-white/[0.03]")
      }
      data-testid={`poll-option-${poll.id}-${option.id}`}
    >
      <div
        className={"absolute inset-y-0 left-0 rounded-md transition-all " +
                   (isMine ? "bg-emerald-500/15" : "bg-white/[0.04]")}
        style={{ width: `${pct}%` }}
        aria-hidden
      />
      <div className="relative flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {isMine && <CheckCircle className="h-3.5 w-3.5 text-emerald-400 shrink-0" />}
          <span className="text-sm text-zinc-200 truncate">
            {option.label}
            <span className="text-zinc-500">{money(option.amount)}</span>
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-[11px] text-zinc-500 tabular-nums">{pct.toFixed(0)}%</span>
          <Badge variant="outline" className="border-white/10 text-zinc-300 tabular-nums h-5">
            {count}
          </Badge>
        </div>
      </div>
    </button>
  );
}

/**
 * BoardPollsCard — directors vote on budget line items, admins create polls,
 * everyone watches tallies tick up in real time via WebSocket.
 */
export function BoardPollsCard({ scope = "all" }) {
  const { user } = useAuth();
  const { properties, currentProperty } = useProperty();
  const { subscribe } = useRealtime();
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    property_id: scope !== "all" ? scope : (currentProperty?.id || properties[0]?.id || ""),
    title: "",
    description: "",
    options: [
      { label: "", amount: "" },
      { label: "", amount: "" },
    ],
  });

  const isAdmin = user?.role === "admin";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/board/polls", { params: { status: "open" } });
      setPolls(data.items || []);
    } catch (e) {
      if (e?.response?.status !== 403 && e?.response?.status !== 401) {
        toast.error(apiErrorMessage(e) || "Failed to load polls");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Subscribe to poll.tally + poll.new events on every property the user can
  // see, so live tallies animate for every socket the moment a vote lands.
  useEffect(() => {
    if (!user || !user.property_ids) return;
    const unsubs = user.property_ids.map((pid) =>
      subscribe(`property:${pid}`, ({ event, payload }) => {
        if (event === "poll.tally") {
          setPolls((prev) => prev.map((p) => p.id === payload.poll_id ? { ...p, tally: payload.tally } : p));
        } else if (event === "poll.new" || event === "poll.closed") {
          load();
        }
      }),
    );
    return () => unsubs.forEach((u) => u && u());
  }, [user, subscribe, load]);

  const create = async () => {
    const clean = form.options
      .map((o) => ({ label: (o.label || "").trim(), amount: o.amount ? Number(o.amount) : undefined }))
      .filter((o) => o.label);
    if (clean.length < 2) return toast.error("Add at least two options");
    setBusy(true);
    try {
      await api.post("/admin/board/polls", {
        property_id: form.property_id,
        title: form.title,
        description: form.description || null,
        options: clean,
      });
      toast.success("Poll opened for voting");
      setDialogOpen(false);
      setForm({
        property_id: form.property_id, title: "", description: "",
        options: [{ label: "", amount: "" }, { label: "", amount: "" }],
      });
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Create failed");
    } finally {
      setBusy(false);
    }
  };

  const closePoll = async (pollId) => {
    if (!window.confirm("Close this poll? No more votes will be accepted.")) return;
    try {
      await api.delete(`/admin/board/polls/${pollId}`);
      toast.success("Poll closed");
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Close failed");
    }
  };

  const vote = async (poll, optionId) => {
    try {
      const { data } = await api.post(`/board/polls/${poll.id}/vote`, { option_id: optionId });
      setPolls((prev) => prev.map((p) => p.id === poll.id ? { ...p, tally: data.tally, my_vote: data.my_vote } : p));
      toast.success("Vote recorded");
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Vote failed");
    }
  };

  const visible = useMemo(() => {
    if (scope === "all") return polls;
    return polls.filter((p) => p.property_id === scope);
  }, [polls, scope]);

  return (
    <Card className="p-5 border-white/10 bg-card" data-testid="board-polls-card">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="overline">Board voting</div>
          <div className="text-base font-semibold font-display mt-0.5 inline-flex items-center gap-2">
            <Vote className="h-4 w-4 text-violet-400" /> Live poll tallies
            <TrendingUp className="h-3 w-3 text-emerald-400 animate-pulse" data-testid="polls-live-indicator" />
          </div>
          <div className="text-xs text-zinc-500 mt-1">
            {isAdmin ? "Open polls for directors to vote on. Tallies update live." :
                       "Cast your vote — every director gets one vote per poll. Change it anytime while open."}
          </div>
        </div>
        {isAdmin && (
          <Button size="sm" onClick={() => setDialogOpen(true)}
                  className="bg-violet-500 hover:bg-violet-600 rounded-full"
                  data-testid="polls-open-create">
            <Plus className="h-3.5 w-3.5 mr-1" /> New poll
          </Button>
        )}
      </div>

      {loading && visible.length === 0 ? (
        <div className="rounded-md border border-dashed border-white/10 p-6 text-center text-xs text-zinc-500">Loading polls…</div>
      ) : visible.length === 0 ? (
        <div className="rounded-md border border-dashed border-white/10 p-6 text-center text-xs text-zinc-500" data-testid="polls-empty">
          No open polls right now. {isAdmin && "Click \"New poll\" to open one."}
        </div>
      ) : (
        <div className="space-y-4">
          {visible.map((poll) => {
            const community = properties.find((p) => p.id === poll.property_id)?.name || poll.property_id;
            return (
              <div key={poll.id} className="rounded-md border border-white/10 p-3" data-testid={`poll-${poll.id}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-zinc-100 truncate">{poll.title}</div>
                    {poll.description && <div className="text-xs text-zinc-500 mt-0.5">{poll.description}</div>}
                    <div className="text-[11px] text-zinc-500 mt-1 inline-flex items-center gap-1">
                      <Users2 className="h-3 w-3" /> {poll.tally?.total_votes || 0} vote{poll.tally?.total_votes === 1 ? "" : "s"} · {community}
                    </div>
                  </div>
                  {isAdmin && (
                    <Button variant="ghost" size="sm" onClick={() => closePoll(poll.id)}
                            className="text-xs text-zinc-400 hover:text-red-300 h-7"
                            data-testid={`poll-close-${poll.id}`}>
                      <X className="h-3 w-3 mr-1" /> Close
                    </Button>
                  )}
                </div>
                <div className="space-y-1.5">
                  {poll.options.map((o) => (
                    <PollBar
                      key={o.id}
                      option={o}
                      tally={poll.tally}
                      poll={poll}
                      myVote={poll.my_vote}
                      onVote={(oid) => vote(poll, oid)}
                      disabled={poll.status !== "open"}
                    />
                  ))}
                </div>
                <PollCommentsThread poll={poll} subscribe={subscribe} />
              </div>
            );
          })}
        </div>
      )}

      {/* Create dialog — admin only */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0a0a0b] border-white/10 text-white sm:max-w-[520px]" data-testid="poll-create-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Vote className="h-4 w-4 text-violet-400" /> New board poll</DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs">
              Add 2+ options with optional dollar amounts. Every seated director gets one vote — tallies update live.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-zinc-400">Community</Label>
              <select
                className="w-full bg-white/[0.04] border border-white/10 rounded-md px-3 py-2 text-sm h-10"
                value={form.property_id}
                onChange={(e) => setForm({ ...form, property_id: e.target.value })}
                data-testid="poll-form-property"
              >
                {properties.map((p) => (
                  <option key={p.id} value={p.id} className="bg-[#0a0a0b]">{p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Question</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                     placeholder="e.g. Approve $18,000 for pool resurfacing?"
                     className="bg-white/[0.04] border-white/10" data-testid="poll-form-title" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Context (optional)</Label>
              <Textarea rows={2} value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                        placeholder="Add background so directors can vote informed."
                        className="bg-white/[0.04] border-white/10" data-testid="poll-form-desc" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400 mb-1">Options</Label>
              <div className="space-y-1.5">
                {form.options.map((o, idx) => (
                  <div key={idx} className="flex items-center gap-2" data-testid={`poll-form-option-${idx}`}>
                    <Input value={o.label}
                           onChange={(e) => setForm({ ...form, options: form.options.map((opt, i) => i === idx ? { ...opt, label: e.target.value } : opt) })}
                           placeholder={`Option ${idx + 1}`}
                           className="bg-white/[0.04] border-white/10 flex-1"
                           data-testid={`poll-form-option-${idx}-label`} />
                    <Input type="number" value={o.amount}
                           onChange={(e) => setForm({ ...form, options: form.options.map((opt, i) => i === idx ? { ...opt, amount: e.target.value } : opt) })}
                           placeholder="Amount ($, optional)"
                           className="bg-white/[0.04] border-white/10 w-40"
                           data-testid={`poll-form-option-${idx}-amount`} />
                    {form.options.length > 2 && (
                      <button onClick={() => setForm({ ...form, options: form.options.filter((_, i) => i !== idx) })}
                              className="text-zinc-500 hover:text-red-300">
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <Button variant="ghost" size="sm" onClick={() => setForm({ ...form, options: [...form.options, { label: "", amount: "" }] })}
                      className="mt-2 text-orange-300 hover:text-orange-200" data-testid="poll-form-add-option">
                <Plus className="h-3 w-3 mr-1" /> Add option
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-white/10">Cancel</Button>
            <Button onClick={create} disabled={busy || !form.title || !form.property_id}
                    className="bg-violet-500 hover:bg-violet-600" data-testid="poll-create-submit">
              {busy ? "Opening…" : "Open poll"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default BoardPollsCard;
